/**
 * Dashboard Toolbar Component
 *
 * Top toolbar with controls for:
 * - Edit mode toggle
 * - Dashboard management
 * - Fullscreen toggle
 * - Add widget button
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import React, { useState } from 'react';
import {
  Edit3,
  Save,
  Plus,
  Maximize,
  Minimize,
  LayoutGrid,
  RotateCcw,
  ChevronDown,
  Check,
  Settings2,
  Tv,
  Target,
} from 'lucide-react';
import { useDashboard } from '../DashboardContext';
import { useTVMode } from '../contexts/TVModeContext';
import DashboardManager from './DashboardManager';
import DashboardTargetsModal from './DashboardTargetsModal';

interface DashboardToolbarProps {
  onAddWidget: () => void;
}

const DashboardToolbar: React.FC<DashboardToolbarProps> = ({ onAddWidget }) => {
  const {
    state,
    toggleEditMode,
    toggleFullscreen,
    resetToDefault,
    loadLayout,
  } = useDashboard();

  const { isEditMode, isFullscreen, currentLayout, availableLayouts } = state;
  const [showLayoutMenu, setShowLayoutMenu] = useState(false);
  const [showDashboardManager, setShowDashboardManager] = useState(false);
  const [showTargetsModal, setShowTargetsModal] = useState(false);

  // TV mode
  const tvMode = useTVMode();
  const tvModeAvailable = true;
  const isTVMode = tvMode.state.config.enabled;
  const toggleTV = tvMode.toggleTVMode;

  return (
    <>
      <div
        className={`
          relative z-50 flex items-center justify-between px-4 py-2
          bg-gray-900/95 backdrop-blur-sm border-b border-gray-700
          transition-all duration-300
          ${isFullscreen ? 'opacity-0 hover:opacity-100' : ''}
        `}
      >
        {/* Left section - Layout selector */}
        <div className="flex items-center gap-3">
          {/* Quick layout switcher */}
          <div className="relative">
            <button
              onClick={() => setShowLayoutMenu(!showLayoutMenu)}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors"
            >
              <LayoutGrid className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-white max-w-[150px] truncate">
                {currentLayout.name}
              </span>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>

            {/* Quick layout dropdown */}
            {showLayoutMenu && (
              <>
                <div className="fixed inset-0 z-[60]" onClick={() => setShowLayoutMenu(false)} />
                <div className="absolute top-full left-0 mt-1 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-[70] overflow-hidden">
                  <div className="p-2 border-b border-gray-700">
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wider px-2">
                      Bytt dashboard
                    </p>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {availableLayouts.map((layout) => (
                      <button
                        key={layout.id}
                        onClick={() => {
                          loadLayout(layout.id);
                          setShowLayoutMenu(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex flex-col items-start">
                          <span className="text-sm text-white truncate">{layout.name}</span>
                          <span className="text-xs text-gray-500">{layout.widgets.length} widgets</span>
                        </div>
                        {layout.id === currentLayout.id && (
                          <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="p-2 border-t border-gray-700">
                    <button
                      onClick={() => {
                        setShowLayoutMenu(false);
                        setShowDashboardManager(true);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-400 hover:bg-gray-700 rounded transition-colors"
                    >
                      <Settings2 className="w-4 h-4" />
                      Administrer dashboards...
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Dashboard manager button */}
          <button
            onClick={() => setShowDashboardManager(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors"
            title="Administrer dashboards"
          >
            <Settings2 className="w-4 h-4" />
            <span className="hidden md:inline">Dashboards</span>
          </button>

          {/* Targets button */}
          <button
            onClick={() => setShowTargetsModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors"
            title="Daglige malsettinger"
          >
            <Target className="w-4 h-4" />
            <span className="hidden md:inline">Mal</span>
          </button>

          {/* Widget count */}
          <span className="text-xs text-gray-500 hidden sm:inline">
            {currentLayout.widgets.length} widgets
          </span>
        </div>

        {/* Center - Title (only in fullscreen) */}
        {isFullscreen && (
          <div className="absolute left-1/2 -translate-x-1/2">
            <h1 className="text-lg font-bold text-white">
              Compass Dashboard
            </h1>
          </div>
        )}

        {/* Right section - Actions */}
        <div className="flex items-center gap-2">
          {/* Reset button (visible in edit mode) */}
          {isEditMode && (
            <button
              onClick={resetToDefault}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors"
              title="Tilbakestill til standard"
            >
              <RotateCcw className="w-4 h-4" />
              <span className="hidden sm:inline">Tilbakestill</span>
            </button>
          )}

          {/* Add widget button (visible in edit mode) */}
          {isEditMode && (
            <button
              onClick={onAddWidget}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Legg til widget</span>
            </button>
          )}

          {/* Edit mode toggle */}
          <button
            onClick={toggleEditMode}
            className={`
              flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors
              ${isEditMode
                ? 'bg-green-600 hover:bg-green-500 text-white'
                : 'bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white border border-gray-700'
              }
            `}
            title={isEditMode ? 'Lagre endringer' : 'Rediger dashboard'}
          >
            {isEditMode ? (
              <>
                <Save className="w-4 h-4" />
                <span className="hidden sm:inline">Lagre</span>
              </>
            ) : (
              <>
                <Edit3 className="w-4 h-4" />
                <span className="hidden sm:inline">Rediger</span>
              </>
            )}
          </button>

          {/* TV mode toggle */}
          {tvModeAvailable && (
            <button
              onClick={() => toggleTV?.()}
              className={`
                flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors
                ${isTVMode
                  ? 'bg-purple-600 hover:bg-purple-500 text-white'
                  : 'bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white border border-gray-700'
                }
              `}
              title={isTVMode ? 'Avslutt TV-modus' : 'TV-modus (auto-rotasjon)'}
            >
              <Tv className="w-4 h-4" />
              <span className="hidden sm:inline">{isTVMode ? 'Avslutt TV' : 'TV-modus'}</span>
            </button>
          )}

          {/* Fullscreen toggle */}
          <button
            onClick={toggleFullscreen}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors"
            title={isFullscreen ? 'Avslutt fullskjerm' : 'Fullskjerm'}
          >
            {isFullscreen ? (
              <Minimize className="w-4 h-4" />
            ) : (
              <Maximize className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Dashboard Manager Modal */}
      <DashboardManager
        isOpen={showDashboardManager}
        onClose={() => setShowDashboardManager(false)}
      />

      {/* Targets Modal */}
      <DashboardTargetsModal
        isOpen={showTargetsModal}
        onClose={() => setShowTargetsModal(false)}
      />
    </>
  );
};

export default DashboardToolbar;
